import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import ProfileService from '../../services/profileService';
import StorageService from '../../services/storageService';
import Button from '../common/Button';
import { AVAILABLE_ROLES, isDoctorRole, normalizeRole } from '../../utils/roleUtils';
import './Profile.css';

const FISCAL_REGIMES = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios' },
  { value: '606', label: '606 - Arrendamiento' },
  { value: '608', label: '608 - Demás ingresos' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', label: '614 - Ingresos por intereses' },
  { value: '616', label: '616 - Sin obligaciones fiscales' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '625', label: '625 - Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza (RESICO)' }
];

const Profile = () => {
  const auth = useAuth();
  // const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    licenseId: '',
    role: 'doctor',
    institution: '',
    specialization: '',
    rfc: '',
    taxRegime: '',
    zipCode: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // Get the user ID and access token from Cognito (OIDC context)
        const userId = auth.user?.profile?.sub;
        const token = auth.user?.access_token;

        // Fetch user profile from API
        const profile = await ProfileService.getUserProfile(userId, token);

        setFormData({
          name: profile.name || '',
          email: profile.email || '',
          phoneNumber: profile.phoneNumber || '',
          licenseId: profile.licenseId || '',
          role: normalizeRole(profile.role),
          institution: profile.institution || '',
          specialization: profile.specialization || '',
          username: profile.username || '',
          rfc: profile.rfc || '',
          taxRegime: profile.taxRegime || '',
          zipCode: profile.zipCode || ''
        });

        // Save organizationId to localStorage for quick access across the app
        if (profile.organizationId) {
          StorageService.setOrganizationId(profile.organizationId);
        }
      } catch (error) {
        setMessage({
          type: 'error',
          text: 'Error loading profile data'
        });
      } finally {
        setInitialLoading(false);
      }
    };

    if (auth.user) {
      loadUserProfile();
    }
  }, [auth.user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    // Validate required fields based on role
    if (isDoctorRole(formData.role) && !formData.licenseId.trim()) {
      setMessage({
        type: 'error',
        text: 'La Cédula Profesional es requerida para usuarios Doctores'
      });
      setIsLoading(false);
      return;
    }

    try {
      // Get the user ID and access token from Cognito (OIDC context)
      const userId = auth.user?.profile?.sub;
      const token = auth.user?.access_token;

      // Make the API call to update the user profile
      await ProfileService.updateUserProfile(userId, formData, token);

      setMessage({
        type: 'success',
        text: 'Perfil actualizado exitosamente'
      });
      setIsEditing(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al actualizar el perfil. Inténtalo de nuevo.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const userId = auth.user?.profile?.sub;
      const token = auth.user?.access_token;

      // Fetch user profile from API
      const profile = await ProfileService.getUserProfile(userId, token);

      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        licenseId: profile.licenseId || '',
        role: normalizeRole(profile.role),
        institution: profile.institution || '',
        specialization: profile.specialization || '',
        rfc: profile.rfc || '',
        taxRegime: profile.taxRegime || '',
        zipCode: profile.zipCode || ''
      });
      
      setIsEditing(false);
      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error loading profile data'
      });
    } finally {
      setIsLoading(false);
    }
  };  

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Mi Perfil</h1>
        <p>Gestiona tu información personal y profesional</p>
      </div>

      {initialLoading ? (
        <div className="profile-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="profile-content">
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              <i className="fas fa-user-md"></i>
            </div>
            <div className="profile-info">
              <h2>{formData.name}</h2>
              <p className="user-email">{formData.username}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <h3>Información Personal</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nombre Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Correo Electrónico *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phoneNumber">Teléfono</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Información Profesional</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">Rol *</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  >
                    {AVAILABLE_ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isDoctorRole(formData.role) && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="licenseId">
                        Cédula Profesional *
                      </label>
                      <input
                        type="text"
                        id="licenseId"
                        name="licenseId"
                        value={formData.licenseId}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="institution">
                        Institución / Escuela
                      </label>
                      <input
                        type="text"
                        id="institution"
                        name="institution"
                        value={formData.institution}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Universidad o institución de estudios"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="specialization">
                        Especialidad o Título
                      </label>
                      <input
                        type="text"
                        id="specialization"
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Especialidad médica o título profesional"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <h3>Información Fiscal</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="rfc">RFC</label>
                  <input
                    type="text"
                    id="rfc"
                    name="rfc"
                    value={formData.rfc}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Registro Federal de Contribuyentes"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="taxRegime">Régimen Fiscal</label>
                  <select
                    id="taxRegime"
                    name="taxRegime"
                    value={formData.taxRegime}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  >
                    <option value="">Seleccione un régimen</option>
                    {FISCAL_REGIMES.map(regime => (
                      <option key={regime.value} value={regime.value}>
                        {regime.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

               <div className="form-row">
                <div className="form-group">
                  <label htmlFor="zipCode">Código Postal</label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Código Postal Fiscal"
                  />
                </div>
              </div>
            </div>
            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}
            <div className="form-actions">
              {!isEditing ? (
                <Button
                  variant="primary"
                  icon="fas fa-edit"
                  onClick={() => setIsEditing(true)}
                >
                  Editar Perfil
                </Button>
              ) : (
                <div className="btn-group">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isLoading}
                    icon="fas fa-save"
                  >
                    {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="profile-security">
          <h3>Seguridad</h3>
          <div className="security-item">
            <div className="security-info">
              <h4>Cambiar Contraseña</h4>
              <p>Para actualizar tu contraseña, utiliza la opción de olvide mi contraseña en la página de inicio de sesión.</p>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Profile;
